import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditApi } from '../../../api/admin/audit';
import { SectionHeader } from '../../../components/SectionHeader';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable } from '../../../components/DataTable';
import { summarizeValues } from '../../../components/AuditHistoryTab';
import { formatDateTime } from '../../../utils/formatDate';
import { useUserName } from '../../../hooks/useUserName';

const entityTypes = [
  'department',
  'position',
  'location_hierarchy',
  'role',
  'role_permission',
  'user_role',
  'user_permission_override',
  'user',
  'user_approval_limit',
  'delegation',
];

/**
 * The backend's /api/admin/audit endpoint only supports looking up
 * history for one specific entityType + entityId at a time (a thin
 * wrapper over AuditEngine.history() - see
 * admin-audit.controller.ts). This page reflects that real shape
 * honestly rather than pretending a combined search exists.
 */
export function AuditPage() {
  const [entityType, setEntityType] = useState('department');
  const [entityId, setEntityId] = useState('');
  const [searched, setSearched] = useState<{ entityType: string; entityId: number } | null>(null);
  const { userName } = useUserName();

  const auditQuery = useQuery({
    queryKey: ['audit', searched?.entityType, searched?.entityId],
    queryFn: () => auditApi.forEntity(searched!.entityType, searched!.entityId),
    enabled: searched !== null,
  });

  return (
    <div className="page">
      <PageHeader title="Audit Trail" description="Look up the change history for a specific record." />

      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (entityId) setSearched({ entityType, entityId: Number(entityId) });
        }}
      >
        <label>
          Entity type
          <select value={entityType} onChange={(e) => setEntityType(e.target.value)}>
            {entityTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label>
          Entity ID
          <input type="number" min="1" value={entityId} onChange={(e) => setEntityId(e.target.value)} />
        </label>
        <button type="submit">Search</button>
      </form>

      {auditQuery.isLoading && <p>Loading…</p>}
      {auditQuery.isError && <p className="error-text">Could not load audit history.</p>}

      {auditQuery.data && (
        <div className="panel">
          <SectionHeader title="Audit History" />
          <div className="panel-body">
        <DataTable
          rows={auditQuery.data.data}
          rowKey={(a) => a.id}
          emptyMessage="No audit history found for that record."
          columns={[
            { header: 'When', render: (a) => formatDateTime(a.created_at) },
            { header: 'Actor', render: (a) => userName(a.user_id) },
            { header: 'Action', render: (a) => a.action },
            { header: 'Old values', render: (a) => summarizeValues(a.old_values) },
            { header: 'New values', render: (a) => summarizeValues(a.new_values) },
            { header: 'IP address', render: (a) => a.ip_address ?? '-' },
          ]}
        />
          </div>
        </div>
      )}
    </div>
  );
}
